// src/components/CompanyLogsPage.js
//
// Company-admin-only activity log viewer. Shows what the caller's own company
// members have done (create / draft / send for review / load to Nezo / delete /
// edit …), newest first, with client-side filters for investigation.
//
// Data: GET /logs/company (gated to company admins, scoped to company members).

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchCompanyLogs } from '../services/api';
import { useCompanyRole } from '../hooks/useCompanyRole';
import './CompanyLogsPage.css';

// Friendly labels for the action_type strings written across the app.
const ACTION_LABELS = {
  SUBMIT_COMPOSITE_REQUEST:           'Submitted for approval',
  SUBMIT_MATERIAL_REQUEST:            'Submitted for approval',
  SAVE_DRAFT_MATERIAL_REQUEST:        'Saved to draft',
  SEND_FOR_REVIEW_MATERIAL_REQUEST:   'Sent for review',
  LOAD_TO_NEZO:                       'Loaded to Nezo',
  REQUEST_COMPOSITE_DELETION:         'Requested deletion',
  REQUEST_MATERIAL_DELETION:          'Requested deletion',
  COMPANY_DISMISS_COMPOSITE:          'Dismissed request',
  REJECT_COMPOSITE_REQUEST:           'Rejected request',
  SUBMIT_SUPPLIER_MATERIAL_REQUEST:   'Submitted product',
  APPROVE_SUPPLIER_MATERIAL_REQUEST:  'Approved product',
  REJECT_SUPPLIER_MATERIAL_REQUEST:   'Rejected product',
  DELETE_MATERIAL:                    'Deleted material',
  DELETE_MATERIAL_SUCCESS:            'Deleted material',
  UPDATE_MATERIAL_SUCCESS:            'Edited material',
  UPDATE_MATERIAL_NAME_SUCCESS:       'Renamed material',
  UPDATE_MATERIAL_DESCRIPTION_SUCCESS:'Edited description',
  UPDATE_MATERIAL_KIND_SUCCESS:       'Changed material type',
  FILE_UPLOAD:                        'Uploaded a file',
};

const humanize = (s) =>
  String(s || '').toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const actionLabel = (a) => ACTION_LABELS[a] || humanize(a);

const parseDetails = (d) => {
  if (!d) return {};
  if (typeof d === 'object') return d;
  try { return JSON.parse(d); } catch { return { raw: String(d) }; }
};

// Material name: prefer the enriched name resolved on the backend, then any name in details.
const matName = (l) => l.material_name || l._details?.materialName || l._details?.name || '';
// Material id: whichever identifier the log captured.
const matId = (d) => d.requestId || d.quantityUnitId || d.materialId || '';

const regionOf = (d) => d.regionName || d.regionId || d.region || '';

const ROLE_LABELS = { admin: 'Admin', reviewer: 'Reviewer', user: 'User' };

function CompanyLogsPage() {
  const navigate = useNavigate();
  const { isCompanyAdmin, loading: roleLoading } = useCompanyRole();

  const [logs, setLogs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const [userFilter, setUserFilter]     = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [fromDate, setFromDate]         = useState('');
  const [toDate, setToDate]             = useState('');
  const [search, setSearch]             = useState('');
  const [expanded, setExpanded]         = useState(null);

  // Once the company role resolves, bounce non-admins out.
  useEffect(() => {
    if (!roleLoading && !isCompanyAdmin) navigate('/unauthorized');
  }, [roleLoading, isCompanyAdmin, navigate]);

  useEffect(() => {
    if (roleLoading || !isCompanyAdmin) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchCompanyLogs();
        const rows = Array.isArray(res.data) ? res.data : [];
        if (!cancelled) {
          setLogs(rows.map((r) => ({ ...r, _details: parseDetails(r.action_details) })));
        }
      } catch (e) {
        console.error('Failed to load company logs:', e);
        if (!cancelled) setError('Failed to load logs. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [roleLoading, isCompanyAdmin]);

  const users   = useMemo(
    () => [...new Set(logs.map((l) => l.user_email).filter(Boolean))].sort(),
    [logs],
  );
  const actions = useMemo(
    () => [...new Set(logs.map((l) => l.action_type).filter(Boolean))].sort(),
    [logs],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const from = fromDate ? new Date(`${fromDate}T00:00:00`) : null;
    const to   = toDate   ? new Date(`${toDate}T23:59:59`)   : null;
    return logs.filter((l) => {
      if (userFilter !== 'all'   && l.user_email !== userFilter)   return false;
      if (actionFilter !== 'all' && l.action_type !== actionFilter) return false;
      const t = new Date(l.created_at);
      if (from && t < from) return false;
      if (to   && t > to)   return false;
      if (q) {
        const hay = `${l.user_email} ${l.action_type} ${actionLabel(l.action_type)} ${matName(l)} ${matId(l._details)} ${JSON.stringify(l._details)}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [logs, userFilter, actionFilter, fromDate, toDate, search]);

  const clearFilters = () => {
    setUserFilter('all'); setActionFilter('all');
    setFromDate(''); setToDate(''); setSearch('');
  };

  // While role is resolving (or we're about to redirect a non-admin)
  if (roleLoading || !isCompanyAdmin) {
    return (
      <div className="clp-page">
        <div className="clp-loading"><div className="clp-spinner" /><p>Checking access…</p></div>
      </div>
    );
  }

  return (
    <div className="clp-page">
      <div className="clp-header">
        <button className="clp-back" onClick={() => navigate('/user/dashboard')}>← Dashboard</button>
        <div className="clp-heading">
          <h1 className="clp-title">📋 Activity Logs</h1>
          <p className="clp-subtitle">Audit your team’s material activity — who did what, and when.</p>
        </div>
      </div>

      {/* Filters */}
      <div className="clp-filters">
        <div className="clp-field">
          <label>User</label>
          <select value={userFilter} onChange={(e) => setUserFilter(e.target.value)}>
            <option value="all">All users</option>
            {users.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>
        <div className="clp-field">
          <label>Action</label>
          <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)}>
            <option value="all">All actions</option>
            {actions.map((a) => <option key={a} value={a}>{actionLabel(a)}</option>)}
          </select>
        </div>
        <div className="clp-field">
          <label>From</label>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </div>
        <div className="clp-field">
          <label>To</label>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </div>
        <div className="clp-field clp-field--grow">
          <label>Search</label>
          <input
            type="text"
            placeholder="Search email, action, material…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <button className="clp-clear" onClick={clearFilters}>Clear</button>
      </div>

      <div className="clp-count">
        {loading ? 'Loading…' : `Showing ${filtered.length} of ${logs.length} entries`}
      </div>

      {/* Body */}
      {loading ? (
        <div className="clp-loading"><div className="clp-spinner" /><p>Loading activity…</p></div>
      ) : error ? (
        <div className="clp-empty clp-empty--error">{error}</div>
      ) : filtered.length === 0 ? (
        <div className="clp-empty">No activity matches your filters.</div>
      ) : (
        <div className="clp-table-wrap">
          <table className="clp-table">
            <thead>
              <tr>
                <th>When</th>
                <th>User</th>
                <th>Role</th>
                <th>Action</th>
                <th>Material</th>
                <th>Region</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => {
                const open = expanded === l.id;
                return (
                  <React.Fragment key={l.id}>
                    <tr className="clp-row" onClick={() => setExpanded(open ? null : l.id)}>
                      <td className="clp-when">{new Date(l.created_at).toLocaleString()}</td>
                      <td className="clp-user">{l.user_email}</td>
                      <td>
                        {l.company_role
                          ? <span className={`clp-role clp-role--${l.company_role}`}>{ROLE_LABELS[l.company_role] || l.company_role}</span>
                          : <span className="clp-muted">—</span>}
                      </td>
                      <td><span className="clp-action">{actionLabel(l.action_type)}</span></td>
                      <td className="clp-material">
                        {matName(l) && <span className="clp-mat-name">{matName(l)}</span>}
                        {matId(l._details) && <span className="clp-mat-id">{matId(l._details)}</span>}
                        {!matName(l) && !matId(l._details) && <span className="clp-muted">—</span>}
                      </td>
                      <td>{regionOf(l._details) || <span className="clp-muted">—</span>}</td>
                      <td className="clp-expand">{open ? '▾' : '▸'}</td>
                    </tr>
                    {open && (
                      <tr className="clp-detail-row">
                        <td colSpan={7}>
                          <pre className="clp-detail">{JSON.stringify(l._details, null, 2)}</pre>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default CompanyLogsPage;
